import React, { ReactNode } from 'react';

import { keys } from '@sola/contentful-model/keys.js';
import { LOCALES_CODES } from '@sola/contentful-model/lib/types.js';
import i18n from '@sola/locales/index.js';
import { AnyObject } from '@sola/schema/dotcom/helpers.js';
import {
  ArticleResponseDto,
  ContentResponseBody,
  ContentResponseDto,
  ContributorResponseDto,
  EmbedResponseDto,
  FlexibleContentDto,
  Media,
  MergeFieldOptions,
  NavResponseDto,
  PageResponseDto,
  PageResponseIndexDto,
  PartnerResponseDto,
  SacFilterResponseDto,
} from '@sola/schema/lib/contentful.js';
import {
  ArticleProps,
  ContributorProps,
  EmbedProps,
  FlexibleContentProps,
  FundProps,
  Link,
  MediaProps,
  MergeData,
  NavigationProps,
  PartnerProps,
  SacFilterProps,
} from '@sola/schema/lib/schema.js';

import {
  convertContentResponseToFlexibleContent,
  convertContentResponseToFund,
  convertContentResponseToLink,
  convertMediaResponseToMedia,
} from './response';
import { getRichText } from './rich-text';
import { mergeFields } from './utils/mergeFields';

const processBodyMergeFields = (
  elements: ReactNode[],
  fieldsToMerge: MergeFieldOptions,
  mergeFallbackOptions?: MergeFieldOptions,
) => {
  const modifiedElements: ReactNode[] = [];

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (typeof element === 'string' || Array.isArray(element)) {
      const string = Array.isArray(element) ? element[0] : element;
      const mergedString = mergeFields(
        string,
        fieldsToMerge,
        mergeFallbackOptions,
      );
      modifiedElements.push(mergedString);
    } else if (React.isValidElement(element)) {
      let { children } = element.props as { children?: ReactNode | undefined };

      if (Array.isArray(children)) {
        const allAreStrings = children.every(
          (child) => typeof child === 'string',
        );
        children = allAreStrings ? children.join('') : children;
      } else {
        children = [children];
      }

      // things like Bold show as a string so we normalize it into an array
      const childrenArray: ReactNode[] = Array.isArray(children)
        ? children
        : [children];
      const modifiedChildren = processBodyMergeFields(
        childrenArray,
        fieldsToMerge,
        mergeFallbackOptions,
      );
      const updatedProps = {
        ...(element.props as AnyObject),
        children: modifiedChildren,
      };
      // @TODO: Using cloneElement is uncommon and can lead to fragile code.
      modifiedElements.push(React.cloneElement(element, updatedProps));
    } else {
      modifiedElements.push(element);
    }
  }

  return modifiedElements;
};

export const processMergeFields = (
  fields: Pick<
    FlexibleContentProps,
    'title' | 'primaryCTA' | 'secondaryCTA' | 'tertiaryCTA' | 'body' | 'items'
  >,
  mergeData?: MergeData,
) => {
  const { t } = i18n;
  const countryName = mergeData?.pageData?.country;
  const fieldsToMerge: MergeFieldOptions = {
    CHILD_NAME:
      mergeData?.richmixChildData?.name.preferred ??
      mergeData?.childDetails?.name.preferred,
    SOURCE_CODE:
      mergeData?.cookies?.find((c) => c.name === 'referrer')?.value ?? '',
    COUNTRY_NAME: countryName ? t(`geo:countries.${countryName}.nameS`) : '',
    CDSP_RATE: mergeData?.campaignData?.cdspOverrideRate?.toString(),
    UNAVAILABLE_CHILD_NAME:
      mergeData?.childName ?? mergeData?.childDetails?.name.preferred,
  };

  const mergeFallbackOptions: MergeFieldOptions = {
    CHILD_NAME: t('priority:mergeFallbacks.childName'),
    COUNTRY_NAME: t('priority:mergeFallbacks.countryName'),
    UNAVAILABLE_CHILD_NAME: {
      default: t('priority:mergeFallbacks.childName'),
      cap: t('priority:mergeFallbacks.childNameCap'),
    },
  };

  const title =
    fields?.title &&
    mergeFields(fields.title, fieldsToMerge, mergeFallbackOptions);

  const primaryCTA = fields?.primaryCTA && {
    ...fields.primaryCTA,
    title: mergeFields(
      fields.primaryCTA.title,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
    link: mergeFields(
      fields.primaryCTA.link,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
  };

  const secondaryCTA = fields?.secondaryCTA && {
    ...fields.secondaryCTA,
    title: mergeFields(
      fields.secondaryCTA.title,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
    link: mergeFields(
      fields.secondaryCTA.link,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
  };

  const tertiaryCTA = fields?.tertiaryCTA && {
    ...fields.tertiaryCTA,
    title: mergeFields(
      fields.tertiaryCTA.title,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
    link: mergeFields(
      fields.tertiaryCTA.link,
      fieldsToMerge,
      mergeFallbackOptions,
    ),
  };

  const body =
    fields?.body &&
    processBodyMergeFields(
      Array.isArray(fields.body) ? fields.body : [fields.body],
      fieldsToMerge,
      mergeFallbackOptions,
    );

  const items = fields?.items?.map((i) => ({
    ...i,
    title:
      i?.title && mergeFields(i.title, fieldsToMerge, mergeFallbackOptions),
    primaryCTA: i?.primaryCTA && {
      ...i.primaryCTA,
      title: mergeFields(
        i.primaryCTA.title,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
      link: mergeFields(i.primaryCTA.link, fieldsToMerge, mergeFallbackOptions),
    },
    secondaryCTA: i?.secondaryCTA && {
      ...i.secondaryCTA,
      title: mergeFields(
        i.secondaryCTA.title,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
      link: mergeFields(
        i.secondaryCTA.link,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
    },
    tertiaryCTA: i?.tertiaryCTA && {
      ...i.tertiaryCTA,
      title: mergeFields(
        i.tertiaryCTA.title,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
      link: mergeFields(
        i.tertiaryCTA.link,
        fieldsToMerge,
        mergeFallbackOptions,
      ),
    },
    body:
      i?.body &&
      processBodyMergeFields(
        Array.isArray(i.body) ? i.body : [i.body],
        fieldsToMerge,
        mergeFallbackOptions,
      ),
  }));

  return {
    ...fields,
    primaryCTA,
    secondaryCTA,
    tertiaryCTA,
    title,
    body,
    items,
  };
};

// @TODO: Unit Test
export const extractArgsContent = (source: ContentResponseDto) => {
  const convertedBody = getRichText(source.body as ContentResponseBody);
  const convertedSource = convertContentResponseToFlexibleContent(source);

  return FlexibleContentProps.parse({
    ...convertedSource,
    body: convertedBody?.content,
  });
};

// @TODO: Unit Test
export const extractArgsSacFilter = (
  source: SacFilterResponseDto,
  mergeFieldData?: MergeData,
) => {
  const relatedContentProps = source.relatedContent
    ? extractArgsContent(source.relatedContent)
    : {};
  return SacFilterProps.parse({
    limit: source.limit || undefined,
    ageMax: source.ageMax || undefined,
    ageMin: source.ageMin || undefined,
    birthDate: source.birthDate || undefined,
    centerId: source.centerId || undefined,
    countries: source.countries || [],
    gender: source.gender || undefined,
    // For the boolean flags, we consider false and undefined to be the same. We only care about true.
    disability: source.disability || undefined,
    hiv: source.hiv || undefined,
    highlyVulnerable: source.highlyVulnerable || undefined,
    longestWaiting: source.longestWaiting || undefined,
    orphan: source.orphan || undefined,
    ...relatedContentProps,
    pathToSacPage: mergeFieldData?.pathToSacPage,
    pathToChildBio: mergeFieldData?.pathToChildBio,
  });
};

// @TODO: Unit Tests
export const extractArgsNavigation = (
  navItem: NavResponseDto,
  useAbsolute?: boolean,
): NavigationProps => {
  const convertedLink = convertContentResponseToLink(
    navItem.link,
    navItem.linkedContentCollection,
    useAbsolute,
  );
  const ctaProps = (() => {
    if (navItem.cta?.__typename === 'FlexibleContent') {
      return extractArgsContent(navItem.cta);
    }
    if (navItem.cta?.__typename === 'SacFilter') {
      return extractArgsSacFilter(navItem.cta);
    }
    return undefined;
  })();

  let { badge } = navItem;
  if (navItem.contentPosition?.includes('myNotificationsLink')) {
    // @TODO: Get a real notifications count;
    const unreadNotificationsCount = 3;
    badge = navItem.badge || {
      text: `${unreadNotificationsCount || 0}`,
      color: 'positive',
    };
  }

  return NavigationProps.parse({
    title: navItem.title || convertedLink?.title || '',
    description: navItem.summary || undefined,
    icon: navItem.icon || undefined,
    link: convertedLink?.link,
    target: convertedLink?.target,
    displayVariant: navItem.displayVariant,
    cta: ctaProps,
    badge: badge || undefined,
    items: navItem.subNavCollection?.items?.map(
      (item) =>
        extractArgsNavigation(
          { ...item, __typename: 'Navigation' },
          useAbsolute,
        ), // @TODO keys.contentTypes.navigation
    ),
  });
};

// @TODO: Unit Test
export const extractArgsEmbed = (source: EmbedResponseDto) => {
  return EmbedProps.parse({
    url: source.embedSettings?.url,
  });
};

// @TODO: Unit Test
export const extractArgsContributor = (source: ContributorResponseDto) => {
  return ContributorProps.parse({
    name: source.name || undefined,
    position: source.position || undefined,
    image: source.image ? { ...source.image, alt: source.name } : undefined,
  });
};

export const extractArgsPartner = (source: PartnerResponseDto) => {
  return PartnerProps.parse({
    title: source.name || undefined,
    slug: source.slug || undefined,
    body: source.shortDescription,
    mediaLogo: source.logo ? { image: { ...source.logo, alt: '' } } : undefined,
    media: source.bannerImage
      ? { image: { ...source.bannerImage, alt: '' } }
      : undefined,
  });
};

// @TODO: Unit Test
export const extractArgsArticle = (source: ArticleResponseDto) => {
  const articleType = source.taxonomyArticleType || 'article';

  const intro = getRichText(source.intro as ContentResponseBody);
  const body = getRichText(source.body as ContentResponseBody, articleType);
  const conclusion = getRichText(source.conclusion as ContentResponseBody);

  const galleryProps = {
    items:
      source.primaryMediaCollection?.items?.reduce(
        (acc: FlexibleContentProps[], item: Media) => {
          const media = convertMediaResponseToMedia(item);
          const parsedMedia = MediaProps.safeParse(media);
          return parsedMedia.success
            ? [...acc, { media: parsedMedia.data }]
            : acc;
        },
        [],
      ) || [],
  } as FlexibleContentProps;

  const contributors = source.contributorsCollection?.items?.map((item) =>
    extractArgsContributor(item),
  );

  const primaryCtaProps = (() => {
    if (source.primaryCta?.__typename === 'FlexibleContent') {
      return extractArgsContent(source.primaryCta);
    }
    return undefined;
  })();
  const secondaryCtaProps = (() => {
    if (source.secondaryCta?.__typename === 'FlexibleContent') {
      return extractArgsContent(source.secondaryCta);
    }
    if (source.secondaryCta?.__typename === 'SacFilter') {
      return extractArgsSacFilter(source.secondaryCta);
    }
    return undefined;
  })();
  const faqProps = source.faq
    ? {
        ...extractArgsContent(source.faq),
        items:
          source.faq.itemsCollection?.items?.map((item) => ({
            ...extractArgsContent(item),
          })) || [],
      }
    : undefined;

  let tableOfContents = [
    ...(intro?.tableOfContents || []),
    ...(body?.tableOfContents || []),
    ...(conclusion?.tableOfContents || []),
  ];
  if (source.taxonomyArticleType === keys.articleTypes.crisisUpdate) {
    tableOfContents = tableOfContents
      .filter((section) => section.id.includes('update-'))
      .map((section: { id: string; title: string; subtitle?: string }) => {
        const date = section.subtitle
          ? new Date(section.subtitle).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            })
          : '';
        return {
          ...section,
          title: `${section.title} - ${date}`,
        };
      });
  }

  return ArticleProps.parse({
    contentfulEntryID: source.sys.id,
    title: source.pageTitle,
    summary: source.summary || undefined,
    publishDate: source.publishDate || source.sys.firstPublishedAt || undefined,
    primaryMedia: galleryProps,
    intro: intro?.content,
    body: body?.content,
    conclusion: conclusion?.content,
    sections: tableOfContents,
    contributors,
    primaryCta: primaryCtaProps,
    secondaryCta: secondaryCtaProps,
    faq: faqProps,
    taxonomyArticleType: source.taxonomyArticleType,
    // todo: do a fallback to a real value
    taxonomyArticleTheme: source.taxonomyArticleTheme || undefined,
    taxonomyTopic: source.taxonomyTopic || undefined,
    taxonomyProgramCountry: source.taxonomyProgramCountry || undefined,
    taxonomyFunnel: source.taxonomyFunnel || undefined,
    taxonomyProgramRegion: source.taxonomyProgramRegion || undefined,
    modifiedDate: source.sys.publishedAt || undefined,
  });
};

// @TODO: Unit Tests
export const extractFundFromPage = (
  entry: PageResponseDto | PageResponseIndexDto,
): FundProps | undefined => {
  const fundContent = entry.referencedContentCollection?.items?.find(
    (item) => item.__typename === 'FlexibleContent' && item.fund,
  ) as FlexibleContentDto | undefined;

  if (!fundContent) {
    return undefined;
  }

  const fund = convertContentResponseToFund(
    fundContent.fund,
    fundContent.referencedContentCollection,
  );

  return fund;
};

// @TODO: Unit Tests
export const getBreadcrumbFromPage = (
  page: PageResponseDto,
  locale: LOCALES_CODES,
): Link[] => {
  return (
    page.pageHierarchy?.ancestors.map((a) => ({
      link: a.path,
      title: a.pageTitle[locale] || a.pageTitle.en || '',
    })) || []
  );
};

// @TODO: Move to utils
export const getPath = (path?: string | string[]) => {
  if (!path) {
    return '/';
  }

  if (Array.isArray(path)) {
    return `/${path
      .filter(Boolean)
      // @TODO: For some reason, some pages get a path segment with a string of "undefined".
      // Perhaps a bug to do with the trailing slash? Middleware?
      // I'm just nixing it here, but it would be nice to solve whatever is causing this.
      .filter((segment) => segment !== 'undefined')
      .join('/')}`;
  }
  return `/${path}`;
};
